import {
  Box,
  Flex,
  FlexAnchor,
  Heading,
  Icon,
  Image,
  lgBumps,
  PrimaryButton,
  PromotionalBannerActivate,
  Typo,
  useModal,
  WriskTheme,
  xsBumps,
  xxsBumps,
} from '@wrisk/ui-components'
import React, { FunctionComponent, useCallback } from 'react'
import { useTheme } from 'styled-components'

import { getCurrentQuote } from '../../../../domain'
import {
  TKey,
  useWriskTranslation,
} from '../../../../infrastructure/internationalisation'
import { getFeatureToggles, useConfig } from '../../../../state/configuration'
import {
  OfferTermsModalContent,
  PromotionBannerActivated,
  PromotionModalContent,
} from '../../organisms/promotion'
import { useProposal } from '../proposalContext'

const tKey = TKey('components.proposal-promotion-banner')

export const ProposalPromotionBanner: FunctionComponent = () => {
  const { t } = useWriskTranslation()
  const theme = useTheme() as WriskTheme

  const modal = useModal()
  const { hidePromotionBanner } = useConfig(getFeatureToggles)
  const { proposal } = useProposal()

  const { promotion } = getCurrentQuote(proposal)

  const onUnlockClick = useCallback(() => {
    promotion &&
      modal.show({
        content: (
          <PromotionModalContent
            promotion={promotion}
            promoCode={promotion.code ?? ''}
            promoDiscount={promotion.promoDiscount ?? 0}
            onCancel={modal.hide}
            onComplete={modal.hide}
          />
        ),
      })
  }, [modal, promotion])

  const onOfferTermsClick = useCallback(() => {
    modal.show({
      content: <OfferTermsModalContent promoCode={promotion?.code ?? ''} />,
    })
  }, [modal, promotion])

  if (hidePromotionBanner || !promotion) {
    return null
  }

  const background = theme.promoImages?.[promotion.code]?.background
  const foreground = theme.promoImages?.[promotion.code]?.foreground

  return promotion.isActive ? (
    <PromotionBannerActivated onOfferTermsClick={onOfferTermsClick} />
  ) : (
    <PromotionalBannerActivate backgroundImage={background}>
      <Box width={[1, 1, 3 / 5]}>
        <Heading variant='h3' as='h3' typoSize='xl' mb={xsBumps}>
          {t(tKey(promotion.code, 'heading'), { amount: promotion.promoDiscount })}
        </Heading>
        <Typo>{t(tKey(promotion.code, 'content'))}</Typo>
        <Flex
          flexDirection={['column', 'column', 'row']}
          justifyContent={['space-between', 'space-between', 'flex-start']}
          alignItems={['flex-start', 'flex-start', 'center']}
          width={1}
          mt={lgBumps}
        >
          <PrimaryButton
            variant='inverted'
            layout='fixed'
            id='activate-promo'
            onClick={onUnlockClick}
          >
            {t(tKey('activate'))}
          </PrimaryButton>
          <FlexAnchor
            mt={[6, 6, 0]}
            ml={[0, 0, 6]}
            onClick={onOfferTermsClick}
            color='foregroundPromoBanner'
          >
            <Icon mr={xxsBumps} icon='info' size='tiny' />
            <Typo typoSize='sm' lineHeight={0}>
              {t(tKey('offerTerms'))}
            </Typo>
          </FlexAnchor>
        </Flex>
      </Box>
      {foreground && (
        <Flex
          position='absolute'
          bottom={[0, 0, '-32px', '-48px']}
          right='0'
          display={['none', 'none', 'block']}
        >
          <Image width={['200px', '200px', '300px', '400px']} src={foreground} />
        </Flex>
      )}
    </PromotionalBannerActivate>
  )
}
